/*
 * Product Hero V2 (sections/main-product-v2.liquid)
 *
 * - <rootmd-v2-pdp>: sticky columns, sticky add to cart bars, theme editor events.
 * - <rootmd-v2-pdp-gallery>: mobile carousel dots and image lightbox.
 * - <rootmd-v2-pdp-buy-box>: purchase option state and optional add-on.
 *
 * The add to cart request itself is handled by assets/theme.js (`[data-btn-addtocart]`),
 * which also refreshes and opens the cart sidebar.
 */
(() => {
  const OPTION_CHANGE_EVENT = 'rootmd-v2-pdp:option-change';
  const DESKTOP_QUERY = '(min-width: 990px)';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RootmdV2Pdp extends HTMLElement {
    connectedCallback() {
      this.columns = Array.from(this.querySelectorAll('[data-pdp-column]'));
      this.stickyBar = this.querySelector('[data-pdp-sticky-bar]');
      this.stickyCta = this.querySelector('[data-pdp-sticky-cta]');
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

      this.updateColumnTops = this.updateColumnTops.bind(this);
      this.onOptionChange = this.onOptionChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onStickyTrigger = this.onStickyTrigger.bind(this);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateColumnTops);
        this.columns.forEach((column) => this.resizeObserver.observe(column));
      }
      window.addEventListener('resize', this.updateColumnTops);
      this.updateColumnTops();

      this.addEventListener(OPTION_CHANGE_EVENT, this.onOptionChange);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.initStickyBars();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.stickyObserver?.disconnect();
      window.removeEventListener('resize', this.updateColumnTops);
      window.clearInterval(this.hubspotInterval);
      this.setHubspotOffset(0);
    }

    /* Each column sticks at the top when it fits the viewport; when it is taller,
       it scrolls until its bottom is visible and then sticks (negative top). */
    updateColumnTops() {
      if (!this.desktopQuery.matches) {
        this.columns.forEach((column) => column.style.removeProperty('--rootmd-v2-pdp-column-top'));
        return;
      }

      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--rootmd-v2-pdp-sticky-offset')) || 24;
      const viewportHeight = window.innerHeight;

      this.columns.forEach((column) => {
        const height = column.offsetHeight;
        const top = height + offset * 2 > viewportHeight ? viewportHeight - height - offset : offset;
        column.style.setProperty('--rootmd-v2-pdp-column-top', `${Math.round(top)}px`);
      });
    }

    get mainButton() {
      return this.querySelector('[data-pdp-atc]');
    }

    initStickyBars() {
      const actions = this.querySelector('[data-pdp-actions]');
      if (!actions || (!this.stickyBar && !this.stickyCta)) return;

      this.querySelectorAll('[data-pdp-sticky-trigger]').forEach((button) => {
        button.addEventListener('click', this.onStickyTrigger);
      });

      this.stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.setStickyVisibility(this.stickyBar, passed, 'is-visible');
          this.setStickyVisibility(this.stickyCta, !(entry.isIntersecting || passed), null);
        });
      });
      this.stickyObserver.observe(actions);

      // HubSpot chat loads asynchronously; keep it above the sticky bar once it exists.
      this.hubspotInterval = window.setInterval(() => {
        if (!document.getElementById('hubspot-messages-iframe-container')) return;
        window.clearInterval(this.hubspotInterval);
        this.syncHubspotOffset();
      }, 500);
    }

    setStickyVisibility(element, visible, visibleClass) {
      if (!element) return;

      if (visibleClass) {
        element.classList.toggle(visibleClass, visible);
      } else {
        element.classList.toggle('is-hidden', !visible);
      }
      element.setAttribute('aria-hidden', visible ? 'false' : 'true');
      element.querySelectorAll('[data-pdp-sticky-trigger]').forEach((button) => {
        button.tabIndex = visible ? 0 : -1;
      });

      if (element === this.stickyBar) this.syncHubspotOffset();
    }

    syncHubspotOffset() {
      const visible = this.stickyBar?.classList.contains('is-visible');
      this.setHubspotOffset(visible ? this.stickyBar.offsetHeight : 0);
    }

    setHubspotOffset(offset) {
      const hubspot = document.getElementById('hubspot-messages-iframe-container');
      if (hubspot) hubspot.style.setProperty('bottom', `${offset}px`, 'important');
    }

    onStickyTrigger() {
      this.mainButton?.click();
    }

    onOptionChange(event) {
      const { label, price } = event.detail || {};
      this.querySelectorAll('[data-pdp-sticky-label]').forEach((element) => {
        if (label) element.textContent = label;
      });
      this.querySelectorAll('[data-pdp-sticky-price]').forEach((element) => {
        if (price) element.textContent = price;
      });
    }

    onBlockSelect(event) {
      const details = event.target.closest('details');
      if (details) details.open = true;
    }
  }

  class RootmdV2PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-pdp-gallery-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-pdp-gallery-dot]'));

      this.initLightbox();

      if (!this.track || this.dots.length === 0) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.lightbox?.open) this.lightbox.close();
    }

    /* Lightbox ---------------------------------------------------------- */

    initLightbox() {
      this.lightbox = this.querySelector('[data-pdp-lightbox]');
      this.openers = Array.from(this.querySelectorAll('[data-pdp-gallery-open]'));
      if (!this.lightbox || this.openers.length === 0 || typeof this.lightbox.showModal !== 'function') return;

      this.lightboxImage = document.createElement('img');
      this.lightboxImage.className = 'rootmd-v2-pdp__lightbox-image';
      this.lightbox.querySelector('[data-pdp-lightbox-stage]')?.append(this.lightboxImage);
      this.lightboxCounter = this.lightbox.querySelector('[data-pdp-lightbox-counter]');

      this.openers.forEach((opener, index) => {
        opener.addEventListener('click', () => this.openLightbox(index));
      });
      this.lightbox.querySelector('[data-pdp-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
      this.lightbox.querySelector('[data-pdp-lightbox-prev]')?.addEventListener('click', () => this.showImage(this.lightboxIndex - 1));
      this.lightbox.querySelector('[data-pdp-lightbox-next]')?.addEventListener('click', () => this.showImage(this.lightboxIndex + 1));

      this.lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.showImage(this.lightboxIndex - 1);
        if (event.key === 'ArrowRight') this.showImage(this.lightboxIndex + 1);
      });

      // Clicking the empty area around the image closes the dialog.
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox || event.target.classList.contains('rootmd-v2-pdp__lightbox-stage')) {
          this.lightbox.close();
        }
      });

      this.lightbox.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0].clientX;
      }, { passive: true });
      this.lightbox.addEventListener('touchend', (event) => {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        if (Math.abs(deltaX) > 50) this.showImage(this.lightboxIndex + (deltaX < 0 ? 1 : -1));
      }, { passive: true });

      this.lightbox.addEventListener('close', () => {
        document.documentElement.style.removeProperty('overflow');
        this.openers[this.lightboxIndex]?.focus({ preventScroll: true });
      });
    }

    openLightbox(index) {
      this.showImage(index);
      document.documentElement.style.overflow = 'hidden';
      this.lightbox.showModal();
    }

    showImage(index) {
      const total = this.openers.length;
      this.lightboxIndex = (index + total) % total;
      const opener = this.openers[this.lightboxIndex];

      // Show the already loaded gallery image right away, then swap in the large version.
      const currentIndex = this.lightboxIndex;
      const zoomSrc = opener.dataset.zoomSrc;
      const preview = opener.querySelector('img');
      this.lightboxImage.src = preview?.currentSrc || zoomSrc;
      this.lightboxImage.alt = opener.dataset.zoomAlt || '';

      const largeImage = new Image();
      largeImage.onload = () => {
        if (this.lightboxIndex === currentIndex) this.lightboxImage.src = zoomSrc;
      };
      largeImage.src = zoomSrc;
      if (this.lightboxCounter) this.lightboxCounter.textContent = `${this.lightboxIndex + 1} / ${total}`;
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    setActive(index) {
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  class RootmdV2PdpBuyBox extends HTMLElement {
    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('[data-pdp-option]'));
      this.sellingPlanInput = this.querySelector('[data-pdp-selling-plan]');
      this.addon = this.querySelector('[data-pdp-addon]');
      this.button = this.querySelector('[data-pdp-atc]');

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);

      this.options.forEach((option) => option.addEventListener('change', this.onOptionChange));
      this.button?.addEventListener('click', this.onButtonClick);

      this.sync();
    }

    get selectedOption() {
      return this.options.find((option) => option.checked) || this.options[0];
    }

    onOptionChange() {
      this.sync();
    }

    /* Mirror the selected option into the form: a selling plan id enables
       the `selling_plan` input, the one-time option disables it. */
    sync() {
      const selected = this.selectedOption;
      if (!selected) return;

      const sellingPlanId = selected.dataset.sellingPlanId;
      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = !sellingPlanId;
        if (sellingPlanId) this.sellingPlanInput.value = sellingPlanId;
      }

      this.options.forEach((option) => {
        option.closest('[data-pdp-option-card]')?.classList.toggle('is-selected', option === selected);
      });

      this.dispatchEvent(
        new CustomEvent(OPTION_CHANGE_EVENT, {
          bubbles: true,
          detail: { label: selected.dataset.label, price: selected.dataset.price },
        })
      );
    }

    /* When the add-on is checked, add it first and then let the theme add the main
       product, so the cart sidebar opens with both items. */
    onButtonClick(event) {
      if (this.skipAddon || !this.addon?.checked) return;

      event.preventDefault();
      event.stopPropagation();

      const root = window.Shopify?.routes?.root || '/';
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.addon.dataset.variantId), quantity: 1 }] }),
      })
        .catch((error) => console.error(error))
        .finally(() => {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-busy');
          this.skipAddon = true;
          this.button.click();
          this.skipAddon = false;
        });
    }
  }

  if (!customElements.get('rootmd-v2-pdp')) customElements.define('rootmd-v2-pdp', RootmdV2Pdp);
  if (!customElements.get('rootmd-v2-pdp-gallery')) customElements.define('rootmd-v2-pdp-gallery', RootmdV2PdpGallery);
  if (!customElements.get('rootmd-v2-pdp-buy-box')) customElements.define('rootmd-v2-pdp-buy-box', RootmdV2PdpBuyBox);
})();
